// Seeded PRNG (mulberry32). Deterministic given a seed, so the same seed always
// produces the same shuffle order - required both for reproducible Vitest
// fixtures and for the "replay this exact deal" affordance.
//
// Engine code must never call Math.random() directly (enforced by an eslint rule
// scoped to src/engine/**) - always thread an rngState value through GameState
// and use the functions below.
//
// rngState is a single 32-bit integer wrapped in a plain object ({ a }) so it's
// JSON-serializable and can live directly inside GameState with no closures.
//
// Kept byte-identical to the space-base and thunderstone repos' own copy of
// this file, deliberately: it's the one piece of engine code all three games
// share, and a divergence here would be invisible until a seed stopped
// reproducing.

function step(a) {
  a |= 0;
  a = (a + 0x6D2B79F5) | 0;
  let t = Math.imul(a ^ (a >>> 15), 1 | a);
  t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
  const value = ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  return [value, a];
}

export function createRngState(seed) {
  return { a: seed | 0 };
}

// Returns [value, nextRngState] - never mutates the input.
export function nextRandom(rngState) {
  const [value, nextA] = step(rngState.a);
  return [value, { a: nextA }];
}

// Returns [integer in [0, max), nextRngState].
export function nextInt(max, rngState) {
  const [value, next] = nextRandom(rngState);
  return [Math.floor(value * max), next];
}

// Fisher-Yates shuffle. Returns [shuffledArray, nextRngState] - does not mutate
// the input array.
export function shuffle(array, rngState) {
  const result = array.slice();
  let state = rngState;
  for (let i = result.length - 1; i > 0; i -= 1) {
    const [rand, nextState] = nextRandom(state);
    state = nextState;
    const j = Math.floor(rand * (i + 1));
    [result[i], result[j]] = [result[j], result[i]];
  }
  return [result, state];
}
